import { parseKeyX, parseKeyY, parseKeyZ, Position, posKeyXYZ, incKeyX, decKeyX, incKeyY, decKeyY, incKeyZ, decKeyZ } from '../voxelMath'
/**
 * Selection logic extracted to avoid circular dependencies
 * This file contains pure functions for selection operations
 */

import { VoxelMap } from '../voxelTypes'

/**
 * Flood fill to select contiguous voxels
 */
export function floodFillSelect(
  startPos: Position,
  voxels: VoxelMap,
  checkSameType: boolean,
  target?: Set<number>,
  mode: string = 'replace'
): Set<number> {
  const selected = target ?? new Set<number>()
  const startKey = posKeyXYZ(startPos.x, startPos.y, startPos.z)
  const startVoxel = voxels.get(startKey)

  if (!startVoxel) return selected

  // Stack for flood fill
  const stack: number[] = [startKey]
  const visited = new Set<number>()
  // Inline type fields for faster same-type checks (only if requested)
  const tType = checkSameType ? startVoxel.type : undefined
  const tColor = checkSameType ? startVoxel.color : undefined
  const subtract = mode === 'subtract'

  // Use Morton neighbor stepping to avoid decode/re-encode

  while (stack.length > 0) {
    const key = stack.pop()!
    if (visited.has(key)) continue
    visited.add(key)

    const voxel = voxels.get(key)
    if (!voxel) continue

    // Check if same type if required
    if (checkSameType && (voxel.type !== tType || voxel.color !== tColor)) continue

    // Apply to target set
    if (subtract) selected.delete(key)
    else selected.add(key)

    // Explore neighbors directly in Morton space (no array allocations)
    {
      const nk = incKeyX(key)
      if (!visited.has(nk)) {
        const nv = voxels.get(nk)
        if (nv && (!checkSameType || (nv.type === tType && nv.color === tColor))) stack.push(nk)
      }
    }
    {
      const nk = decKeyX(key)
      if (!visited.has(nk)) {
        const nv = voxels.get(nk)
        if (nv && (!checkSameType || (nv.type === tType && nv.color === tColor))) stack.push(nk)
      }
    }
    {
      const nk = incKeyY(key)
      if (!visited.has(nk)) {
        const nv = voxels.get(nk)
        if (nv && (!checkSameType || (nv.type === tType && nv.color === tColor))) stack.push(nk)
      }
    }
    {
      const nk = decKeyY(key)
      if (!visited.has(nk)) {
        const nv = voxels.get(nk)
        if (nv && (!checkSameType || (nv.type === tType && nv.color === tColor))) stack.push(nk)
      }
    }
    {
      const nk = incKeyZ(key)
      if (!visited.has(nk)) {
        const nv = voxels.get(nk)
        if (nv && (!checkSameType || (nv.type === tType && nv.color === tColor))) stack.push(nk)
      }
    }
    {
      const nk = decKeyZ(key)
      if (!visited.has(nk)) {
        const nv = voxels.get(nk)
        if (nv && (!checkSameType || (nv.type === tType && nv.color === tColor))) stack.push(nk)
      }
    }
  }

  return selected
}

/**
 * Calculate bounding box center (rounded to nearest voxel)
 */
export function calculateBoundingBoxCenter(
  boundingBox: { min: Position; max: Position } | null
): Position | null {
  if (!boundingBox) return null
  return {
    x: Math.floor((boundingBox.min.x + boundingBox.max.x) / 2 + 0.5),
    y: Math.floor((boundingBox.min.y + boundingBox.max.y) / 2 + 0.5),
    z: Math.floor((boundingBox.min.z + boundingBox.max.z) / 2 + 0.5)
  }
}

/**
 * Select all voxels within a rectangular bound
 */
export function selectVoxelsInBounds(
  min: Position,
  max: Position,
  voxels: VoxelMap,
  _bounds: Position,
  target?: Set<number>,
  mode: string = 'replace'
): Set<number> {
  const out = target ?? new Set<number>()
  const minx = min.x, miny = min.y, minz = min.z
  const maxx = max.x, maxy = max.y, maxz = max.z
  // Iterate existing voxels (sparse-friendly) and filter by AABB (early-outs)
  for (const key of voxels.keys()) {
    const x = parseKeyX(key)
    if (x < minx || x > maxx) continue
    const y = parseKeyY(key)
    if (y < miny || y > maxy) continue
    const z = parseKeyZ(key)
    if (z < minz || z > maxz) continue
    if (mode === 'subtract') out.delete(key)
    else out.add(key)
  }
  return out
}
